import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useState } from "react";
import axios from "axios";
import { GlobalState } from "context/GlobalContext";
import { admin, userTypes } from "data/authData";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";
import RoleAccessSection from "../shared/RoleAccessSection";
import UserProfileFields from "../shared/UserProfileFields";
import { modalCancelBtnSx, modalSaveBtnSx, userUi } from "../shared/userUi";

const UserInfoModal = ({
	readOnly,
	selectedUser,
	allUsers,
	setAllUsers,
	setEditModalOpen,
	setReadOnly,
}) => {
	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();
	const [user, setUser] = useState(selectedUser);
	const [roles, setRoles] = useState({ ...(selectedUser.role || {}) });

	const showSnack = (message, severity = "error") => {
		setSnackBarOpen(true);
		setSnackBar({
			...snackBar,
			message,
			severity,
			anchorOrigin: { horizontal: "center", vertical: "top" },
		});
	};

	const handleCancelEditClick = () => {
		setUser(selectedUser);
		setRoles({ ...(selectedUser.role || {}) });
		setReadOnly(true);
	};

	const handleAddRole = (roleToAdd) => {
		if (roles[roleToAdd]) {
			showSnack("Role already exists");
			return;
		}
		if (user.type === userTypes.external) {
			const other_data = user?.other_data
				? { ...user.other_data, root_path: roleToAdd }
				: { root_path: roleToAdd };
			setUser({ ...user, other_data });
			setRoles({ [roleToAdd]: true });
			return;
		}
		setRoles({ ...roles, [roleToAdd]: true });
	};

	const onFormSubmit = async (e) => {
		e.preventDefault();
		if (!Object.keys(roles).length) {
			showSnack("Add at least one role");
			return;
		}

		const apibody = { ...user, role: roles };
		try {
			await axios.put(`/api/auth/user_update/${user.id}`, apibody);
			showSnack("User updated", "success");
			setAllUsers([apibody, ...allUsers.filter((row) => row.id !== selectedUser.id)]);
			setReadOnly(true);
			setEditModalOpen(false);
		} catch (err) {
			showSnack(parseErrMsgFromError(err) || "Unable to update user details");
		}
	};

	return (
		<Box component="form" onSubmit={onFormSubmit} sx={{ width: "100%" }}>
			<Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, pb: 1 }}>
				<UserProfileFields user={user} setUser={setUser} readOnly={readOnly} lockType />
				<RoleAccessSection
					roles={roles}
					onRolesChange={setRoles}
					onAdd={handleAddRole}
					onAddAdmin={() => handleAddRole(admin)}
					userType={user.type}
					readOnly={readOnly}
				/>
			</Box>
			<Box
				sx={{
					display: "flex",
					justifyContent: "flex-end",
					flexWrap: "wrap",
					gap: 1,
					pt: 2.5,
					mt: 1,
					borderTop: `1px solid ${userUi.border}`,
				}}>
				{readOnly ? (
					<Button variant="contained" onClick={() => setReadOnly(false)} sx={modalSaveBtnSx}>
						Edit data
					</Button>
				) : (
					<>
						<Button variant="outlined" onClick={handleCancelEditClick} sx={modalCancelBtnSx}>
							Cancel edit
						</Button>
						<Button variant="contained" type="submit" sx={modalSaveBtnSx}>
							Save changes
						</Button>
					</>
				)}
			</Box>
		</Box>
	);
};

export default UserInfoModal;
